import { useMemo, type ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import type { AgendaSettings } from "@bookdental/schemas";
import { buildSlots, comHorarios, minutesToTime, timeToMinutes } from "@/lib/agenda-time";
import { CampoDaConsulta, bareInputCls } from "./CampoDaConsulta";
import { DatePickerButton } from "./SeletorDeData";
import type { ConflitoDaConsulta } from "@/lib/appointment-conflict";

type Quando = { date: string; startTime: string; endTime: string };

/** Data, início, fim e a sala — e o aviso quando a sala ou o profissional já estão ocupados no horário. */
export function QuandoDaConsulta({
  valor,
  onChange,
  settings,
  conflito,
  sala,
}: {
  valor: Quando;
  onChange: (v: Partial<Quando>) => void;
  settings: AgendaSettings;
  conflito: ConflitoDaConsulta;
  sala?: ReactNode;
}) {
  const slots = useMemo(() => buildSlots(settings.startHour, settings.endHour, 15), [settings.startHour, settings.endHour]);
  // O horário que a consulta JÁ tem sempre entra na lista: consulta fora do expediente (ou fora da
  // grade de 15 min) abria com o primeiro horário selecionado, e salvar sem perceber a movia. O fim
  // também aceita o horário de fechamento — a grade para no último início possível.
  const opcoesDeInicio = useMemo(() => comHorarios(slots, valor.startTime), [slots, valor.startTime]);
  const opcoesDeFim = useMemo(() => comHorarios(slots, settings.endHour, valor.endTime), [slots, settings.endHour, valor.endTime]);

  const mudarInicio = (novoInicio: string) => {
    // Escolher o início arrasta o fim junto, preservando a duração atual (30 min no padrão; a de
    // serviço, se houver) — o fim continua editável depois.
    const duracaoAtual = timeToMinutes(valor.endTime) - timeToMinutes(valor.startTime);
    const duracao = duracaoAtual > 0 ? duracaoAtual : 30;
    const fimMax = timeToMinutes(settings.endHour);
    const inicio = timeToMinutes(novoInicio);
    // Corta no fechamento só quem começa dentro do expediente; quem já começa depois dele
    // (consulta fora do horário) mantém a duração.
    const fim = inicio < fimMax ? Math.min(inicio + duracao, fimMax) : inicio + duracao;
    onChange({ startTime: novoInicio, endTime: minutesToTime(Math.min(fim, 24 * 60 - 1)) });
  };

  return (
    <>
      {/* No celular a data fica sozinha na linha: espremida em 1/3 de 390px o rótulo virava
          "A..." / "Q...". Início e Fim dividem a linha seguinte. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <CampoDaConsulta label="Data *" className="col-span-2 sm:col-span-1">
          <DatePickerButton value={valor.date} onChange={(date) => onChange({ date })} />
        </CampoDaConsulta>
        <CampoDaConsulta label="Início *">
          <select className={`${bareInputCls} cursor-pointer`} value={valor.startTime} onChange={(e) => mudarInicio(e.target.value)}>
            {opcoesDeInicio.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </CampoDaConsulta>
        <CampoDaConsulta label="Fim *">
          <select className={`${bareInputCls} cursor-pointer`} value={valor.endTime} onChange={(e) => onChange({ endTime: e.target.value })}>
            {opcoesDeFim.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </CampoDaConsulta>
      </div>

      {sala}

      {conflito.sala && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {conflito.sala.nome} já está ocupada das {conflito.sala.startTime} às {conflito.sala.endTime}. Escolha outra sala ou outro horário.
          </span>
        </div>
      )}

      {conflito.dentista && (
        <div
          className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
            settings.conflictBehavior === "block" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {settings.conflictBehavior === "block"
              ? "Esse profissional já tem outra consulta nesse horário. Altere antes de salvar."
              : "Esse profissional já tem outra consulta nesse horário — salva mesmo assim?"}
          </span>
        </div>
      )}
    </>
  );
}
